import { useCallback, useEffect, useRef } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { useRealtime } from '@/lib/realtime/core/useRealtime'
import { useOrganizationStore } from '@/store/organization.store'
import { useQueryClient } from '@tanstack/react-query'

import { SocketEvent } from '@scrambl/shared'

import { subscribeWorkflowInvalidation } from '../workflow-query-invalidation'

interface UseWorkflowsRealtimeOptions {
  enabled?: boolean
  selectedWorkflowId?: string | null
  selectedRunId?: string | null
  suspendDetailUpdates?: boolean
  /** The application listener already owns its organization room. */
  joinOrganizationRoom?: boolean
}

export function useWorkflowsRealtime({
  enabled = true,
  selectedWorkflowId,
  suspendDetailUpdates = false,
  joinOrganizationRoom = true,
}: UseWorkflowsRealtimeOptions = {}) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id
  const scope = useWorkspaceSession()
  const client = useQueryClient()
  const subscription = useRef<ReturnType<
    typeof subscribeWorkflowInvalidation
  > | null>(null)
  const { socket, isReady, joinRoom, leaveRoom } = useRealtime({
    suppressLogging: true,
  })
  useEffect(() => {
    if (!enabled || !scope.enabled) return
    const entry = subscribeWorkflowInvalidation(client, scope)
    subscription.current = entry
    return () => {
      entry.dispose()
      subscription.current = null
    }
  }, [client, enabled, scope])
  useEffect(() => {
    subscription.current?.suspend(
      suspendDetailUpdates ? (selectedWorkflowId ?? null) : null,
    )
  }, [enabled, scope, selectedWorkflowId, suspendDetailUpdates])
  const connection = useRef({ seen: false, ready: false })
  useEffect(() => {
    if (isReady && connection.current.seen && !connection.current.ready)
      subscription.current?.refreshAll()
    connection.current = {
      seen: connection.current.seen || isReady,
      ready: isReady,
    }
  }, [isReady])
  const notify = useCallback(
    (
      data: Record<string, unknown>,
      change: 'definition' | 'run' | 'delete',
    ) => {
      if (
        !scope.enabled ||
        scope.signal.aborted ||
        !orgId ||
        data?.organizationId !== orgId ||
        typeof data.id !== 'string' ||
        !data.id
      )
        return
      const id = change === 'run' ? data.workflowId : data.id
      if (typeof id !== 'string' || !id) return
      const eventId = data._eventId ?? data.eventId
      subscription.current?.notify(
        id,
        change,
        typeof eventId === 'string' ? eventId : undefined,
      )
    },
    [orgId, scope],
  )
  useEffect(() => {
    if (!enabled || !scope.enabled || !socket || !isReady || !orgId) return
    const changed = (data: Record<string, unknown>) =>
      notify(data, 'definition')
    const removed = (data: Record<string, unknown>) => notify(data, 'delete')
    const run = (data: Record<string, unknown>) => notify(data, 'run')
    if (joinOrganizationRoom) joinRoom('organization', orgId)
    socket.on(SocketEvent.WORKFLOW_CREATED, changed)
    socket.on(SocketEvent.WORKFLOW_UPDATED, changed)
    socket.on(SocketEvent.WORKFLOW_DELETED, removed)
    socket.on(SocketEvent.WORKFLOW_RUN_CREATED, run)
    socket.on(SocketEvent.WORKFLOW_RUN_UPDATED, run)
    return () => {
      socket.off(SocketEvent.WORKFLOW_CREATED, changed)
      socket.off(SocketEvent.WORKFLOW_UPDATED, changed)
      socket.off(SocketEvent.WORKFLOW_DELETED, removed)
      socket.off(SocketEvent.WORKFLOW_RUN_CREATED, run)
      socket.off(SocketEvent.WORKFLOW_RUN_UPDATED, run)
      if (joinOrganizationRoom) leaveRoom('organization', orgId)
    }
  }, [
    enabled,
    scope.enabled,
    socket,
    isReady,
    orgId,
    notify,
    joinOrganizationRoom,
    joinRoom,
    leaveRoom,
  ])
}
